import { z } from 'zod';
import {
  WhatsAppDeliveryStateSchema,
  WhatsAppFailureReasonSchema,
  WhatsAppSubjectTypeSchema,
} from '../communications/whatsapp.schemas';
import { MessengerLinkDisconnectReasonSchema } from '../communications/messenger-link.schemas';
import { MessengerSchema } from './messenger-outcome';

export const WhatsAppStatusReportedPayloadSchema = z.object({
  providerMessageId: z.string().min(1),
  status: z.enum(['DELIVERED', 'FAILED']),
  reportedAt: z.iso.datetime(),
  providerErrorCode: z.number().int().nullable(),
  providerErrorDetail: z.string().nullable(),
});
export type WhatsAppStatusReportedPayload = z.infer<typeof WhatsAppStatusReportedPayloadSchema>;

export const WhatsAppMessageReceivedPayloadSchema = z.object({
  providerMessageId: z.string().min(1),
  senderWaId: z.string().min(1),
  profileName: z.string().nullable(),
  body: z.string().nullable(),
  /** The Meta type token when it is one immoOS cannot render; null for a plain text message. */
  unsupportedType: z.string().nullable(),
  receivedAt: z.iso.datetime(),
});
export type WhatsAppMessageReceivedPayload = z.infer<typeof WhatsAppMessageReceivedPayloadSchema>;

export const WhatsAppDeliveryStateChangedPayloadSchema = z.object({
  workspaceId: z.uuid(),
  subjectType: WhatsAppSubjectTypeSchema,
  communicationId: z.uuid(),
  deliveryState: WhatsAppDeliveryStateSchema,
  failureReason: WhatsAppFailureReasonSchema.nullable(),
  /** When Meta confirmed delivery. Null unless `deliveryState` is `DELIVERED`. */
  deliveredAt: z.iso.datetime().nullable(),
  changedAt: z.iso.datetime(),
});
export type WhatsAppDeliveryStateChangedPayload = z.infer<typeof WhatsAppDeliveryStateChangedPayloadSchema>;

// Binding a messenger account to a member is an authentication change (IMM-1040.1).
// Neither payload carries the account: the audit log is not where a member's
// private number should be copied. `messenger` defaults to 'whatsapp' so rows
// already in the outbox before IMM-1127.1 still parse.
export const WhatsAppMemberLinkedPayloadSchema = z.object({
  linkId: z.uuid(),
  workspaceId: z.uuid(),
  memberId: z.uuid(),
  messenger: MessengerSchema.default('whatsapp'),
  occurredAt: z.iso.datetime(),
});
export type WhatsAppMemberLinkedPayload = z.infer<typeof WhatsAppMemberLinkedPayloadSchema>;

export const WhatsAppMemberUnlinkedPayloadSchema = z.object({
  linkId: z.uuid(),
  workspaceId: z.uuid(),
  memberId: z.uuid(),
  messenger: MessengerSchema.default('whatsapp'),
  reason: MessengerLinkDisconnectReasonSchema,
  occurredAt: z.iso.datetime(),
});
export type WhatsAppMemberUnlinkedPayload = z.infer<typeof WhatsAppMemberUnlinkedPayloadSchema>;

// A connected member's inbound text (IMM-1040.2). `affirmative` is read from
// IMM-1040.3 on, but carried from the start so the payload never changes shape
// mid-feature (WhatsAppCopy.isAffirmative — the German "Ja" or English "Yes").
export const WhatsAppMemberMessageRecordedPayloadSchema = z.object({
  chatMessageId: z.uuid(),
  linkId: z.uuid(),
  workspaceId: z.uuid(),
  memberId: z.uuid(),
  messenger: MessengerSchema.default('whatsapp'),
  text: z.string().min(1).max(4096),
  affirmative: z.boolean(),
  receivedAt: z.iso.datetime(),
});
export type WhatsAppMemberMessageRecordedPayload = z.infer<typeof WhatsAppMemberMessageRecordedPayloadSchema>;
